import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Eye, EyeOff, Loader2, Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const btn =
  "inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm transition-colors hover:bg-card disabled:opacity-50";
const field =
  "w-full rounded-xl border border-input bg-card px-3 py-2 outline-none focus:ring-2 focus:ring-ring";
const EMPTY = { question: "", options: ["", "", "", ""], correct_index: -1, category: "", difficulty: "medium", explanation: "" };

const dayLabel = (iso, i) => {
  const d = new Date(`${iso}T12:00:00`);
  const name = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return i === 0 ? `Today, ${name}` : i === 1 ? `Tomorrow, ${name}` : name;
};

function ConfirmButton({ label, onConfirm, disabled }) {
  const [sure, setSure] = useState(false);
  if (!sure) {
    return (
      <button className={btn} disabled={disabled} onClick={() => setSure(true)}>
        {label}
      </button>
    );
  }
  return (
    <span className="inline-flex gap-1">
      <button className={`${btn} border-red-500 text-red-500`} onClick={() => { setSure(false); onConfirm(); }}>
        Yes, {label.toLowerCase()}
      </button>
      <button className={btn} onClick={() => setSure(false)}>Cancel</button>
    </span>
  );
}

function Badge({ children, tone = "" }) {
  return <span className={`rounded-full border border-border px-2 py-0.5 text-xs ${tone}`}>{children}</span>;
}

function QuestionPreview({ q }) {
  return (
    <div className="rounded-xl border border-border bg-background p-3 text-sm">
      <p className="font-medium">{q.question}</p>
      <ul className="mt-2 space-y-1">
        {q.options.map((o, i) => (
          <li key={i} className={i === q.correct_index ? "font-medium text-emerald-500" : "text-muted-foreground"}>
            {"ABCD"[i]}. {o} {i === q.correct_index && <Check className="inline h-3.5 w-3.5" aria-label="correct" />}
          </li>
        ))}
      </ul>
      {q.explanation && <p className="mt-2 text-muted-foreground">{q.explanation}</p>}
    </div>
  );
}

function QuestionForm({ initial, categories, difficulties, busy, onSave, onCancel }) {
  const [f, setF] = useState(initial || EMPTY);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const setOpt = (i, v) => set({ options: f.options.map((o, j) => (j === i ? v : o)) });
  return (
    <form
      className="space-y-3 rounded-2xl border border-border bg-card p-4"
      onSubmit={(e) => { e.preventDefault(); onSave(f, "draft"); }}
      data-testid="trivia-form"
    >
      <label className="block text-sm">
        Question
        <textarea className={`${field} mt-1`} rows={2} value={f.question} onChange={(e) => set({ question: e.target.value })} />
      </label>
      <fieldset>
        <legend className="text-sm">Answers (tick the correct one)</legend>
        {f.options.map((o, i) => (
          <div key={i} className="mt-2 flex items-center gap-2">
            <input type="radio" name="correct" checked={f.correct_index === i} onChange={() => set({ correct_index: i })}
                   aria-label={`Option ${"ABCD"[i]} is correct`} />
            <input className={field} value={o} placeholder={`Option ${"ABCD"[i]}`} onChange={(e) => setOpt(i, e.target.value)}
                   aria-label={`Option ${"ABCD"[i]}`} />
          </div>
        ))}
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          Category
          <select className={`${field} mt-1`} value={f.category} onChange={(e) => set({ category: e.target.value })}>
            <option value="">Choose...</option>
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          Difficulty
          <select className={`${field} mt-1`} value={f.difficulty} onChange={(e) => set({ difficulty: e.target.value })}>
            {difficulties.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <label className="block text-sm">
        Explanation (shown after answering)
        <textarea className={`${field} mt-1`} rows={2} value={f.explanation || ""} onChange={(e) => set({ explanation: e.target.value })} />
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={btn} disabled={busy}>Save as draft</button>
        <button type="button" className={btn} disabled={busy} onClick={() => onSave(f, "published")}>
          Save and publish
        </button>
        <button type="button" className={btn} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function Picker({ date, published, busy, onSave, onCancel }) {
  const [sel, setSel] = useState([]);
  const [search, setSearch] = useState("");
  const rows = useMemo(
    () => published.filter((q) => q.question.toLowerCase().includes(search.toLowerCase())),
    [published, search]
  );
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 5 ? [...s, id] : s));
  return (
    <div className="mt-3 rounded-xl border border-border bg-background p-3" data-testid="trivia-picker">
      <p className="text-sm font-medium">Choose 5 questions for {date} ({sel.length} of 5)</p>
      <input className={`${field} mt-2`} placeholder="Search questions" value={search} onChange={(e) => setSearch(e.target.value)} />
      <ul className="mt-2 max-h-64 space-y-1 overflow-auto">
        {rows.map((q) => (
          <li key={q.id}>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={sel.includes(q.id)} onChange={() => toggle(q.id)} />
              <span>{q.question} <span className="text-xs text-muted-foreground">({q.category}{q.last_used ? `, last used ${q.last_used}` : ""})</span></span>
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2">
        <button className={btn} disabled={busy || sel.length !== 5} onClick={() => onSave(sel)}>Save this quiz</button>
        <button className={btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function AdminTrivia() {
  const [view, setView] = useState("schedule");
  const [qs, setQs] = useState(null);
  const [meta, setMeta] = useState({ categories: [], difficulties: [] });
  const [sched, setSched] = useState(null);
  const [filter, setFilter] = useState({ status: "", category: "", q: "" });
  const [editing, setEditing] = useState(null); // null | "new" | question object
  const [picking, setPicking] = useState("");
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);

  const fail = (e) => toast.error(apiError(e.response?.data?.detail) || e.message);

  const loadQs = useCallback(async () => {
    const r = await api.get("/admin/trivia/questions");
    setQs(r.data.questions);
    setMeta({ categories: r.data.categories, difficulties: r.data.difficulties });
  }, []);
  const loadSched = useCallback(async () => {
    const r = await api.get("/admin/trivia/schedule", { params: { days: 14 } });
    setSched(r.data);
  }, []);
  const reload = useCallback(() => Promise.all([loadQs(), loadSched()]), [loadQs, loadSched]);

  useEffect(() => {
    reload().catch(fail);
  }, [reload]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      await reload();
      if (ok) toast.success(ok);
      return true;
    } catch (e) {
      fail(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async (f, status) => {
    const body = { ...f, status };
    const done = await act(
      () => (editing && editing !== "new" ? api.put(`/admin/trivia/questions/${editing.id}`, body) : api.post("/admin/trivia/questions", body))
        .then(async (r) => {
          if (editing && editing !== "new" && status === "published" && editing.status !== "published") {
            await api.post(`/admin/trivia/questions/${editing.id}/status`, { status: "published" });
          }
        }),
      status === "published" ? "Saved and published" : "Saved"
    );
    if (done) setEditing(null);
  };

  const published = useMemo(() => (qs || []).filter((q) => q.status === "published"), [qs]);
  const shown = useMemo(() => {
    const s = filter.q.toLowerCase();
    return (qs || []).filter(
      (q) => (!filter.status || q.status === filter.status) && (!filter.category || q.category === filter.category) &&
             (!s || q.question.toLowerCase().includes(s))
    );
  }, [qs, filter]);

  if (!qs || !sched) {
    return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C89B3C]" /></div>;
  }
  const bank = sched.bank;

  return (
    <div className="space-y-6" data-testid="admin-trivia">
      <section className="rounded-2xl border border-border bg-card p-4" data-testid="trivia-bank">
        <p className="text-sm">
          <b>{bank.published}</b> published · <b>{bank.draft}</b> drafts · <b>{bank.retired}</b> retired · about{" "}
          <b>{bank.days_of_supply}</b> day{bank.days_of_supply === 1 ? "" : "s"} of fresh quizzes left
        </p>
        {bank.needed > 0 && (
          <p className="mt-2 flex items-center gap-2 text-sm text-amber-500">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Visitors can't play yet. Publish at least {bank.needed} more question{bank.needed === 1 ? "" : "s"}.
          </p>
        )}
        {bank.needed === 0 && bank.days_of_supply < 7 && (
          <p className="mt-2 flex items-center gap-2 text-sm text-amber-500">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Running low. Add more questions soon or quizzes will start repeating questions.
          </p>
        )}
      </section>

      <div className="flex gap-2" role="tablist">
        {["schedule", "questions"].map((v) => (
          <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)}
                  className={`${btn} ${view === v ? "border-[#C89B3C] text-[#C89B3C]" : ""}`}>
            {v === "schedule" ? "Daily schedule" : `Question bank (${qs.length})`}
          </button>
        ))}
      </div>

      {view === "schedule" && (
        <section data-testid="trivia-schedule">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Days with no quiz are filled automatically when the first visitor arrives. Auto-fill them now to preview them.
            </p>
            <button className={btn} disabled={busy} data-testid="trivia-autofill"
                    onClick={() => act(async () => {
                      const r = await api.post("/admin/trivia/schedule/autofill", { days: 14 });
                      if (r.data.stopped_not_enough_questions) toast.warning("Stopped: not enough published questions.");
                      if (r.data.repeated.length) toast.warning(`${r.data.repeated.length} day(s) reuse earlier questions. Add more questions.`);
                    }, "Next 14 days filled")}>
              <Wand2 className="h-4 w-4" aria-hidden="true" /> Auto-fill 14 days
            </button>
          </div>
          <ul className="mt-4 space-y-3">
            {sched.days.map((d, i) => (
              <li key={d.date} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{dayLabel(d.date, i)}</span>
                  {d.scheduled ? (
                    <>
                      <Badge>{d.source === "admin" ? "Chosen by you" : d.source === "rollover" ? "Rolled over (unplayed)" : "Auto-picked"}</Badge>
                      {d.repeats > 0 && <Badge tone="text-amber-500">{d.repeats} reused</Badge>}
                      {i === 0 && <Badge>{d.plays} played</Badge>}
                    </>
                  ) : (
                    <Badge>Not set (auto on the day)</Badge>
                  )}
                  <span className="ml-auto flex flex-wrap gap-1">
                    {d.scheduled && (
                      <button className={btn} onClick={() => setOpen((o) => ({ ...o, [d.date]: !o[d.date] }))}>
                        {open[d.date] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Preview
                      </button>
                    )}
                    {!(i === 0 && d.plays > 0) && (
                      <button className={btn} onClick={() => setPicking(picking === d.date ? "" : d.date)}>
                        {d.scheduled ? "Replace" : "Choose questions"}
                      </button>
                    )}
                    {d.scheduled && !(i === 0 && d.plays > 0) && (
                      <ConfirmButton label="Remove" disabled={busy}
                                     onConfirm={() => act(() => api.delete(`/admin/trivia/schedule/${d.date}`), "Quiz removed")} />
                    )}
                  </span>
                </div>
                {open[d.date] && d.questions && (
                  <div className="mt-3 space-y-2">{d.questions.map((q) => <QuestionPreview key={q.id} q={q} />)}</div>
                )}
                {picking === d.date && (
                  <Picker date={d.date} published={published} busy={busy} onCancel={() => setPicking("")}
                          onSave={async (ids) => {
                            const ok = await act(() => api.put(`/admin/trivia/schedule/${d.date}`, { question_ids: ids }), "Quiz saved");
                            if (ok) setPicking("");
                          }} />
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {view === "questions" && (
        <section data-testid="trivia-questions">
          <div className="flex flex-wrap gap-2">
            <input className={`${field} min-w-0 flex-1`} placeholder="Search" value={filter.q}
                   onChange={(e) => setFilter({ ...filter, q: e.target.value })} aria-label="Search questions" />
            <select className={`${field} w-auto`} value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} aria-label="Status">
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="retired">Retired</option>
            </select>
            <select className={`${field} w-auto`} value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} aria-label="Category">
              <option value="">All categories</option>
              {meta.categories.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button className={btn} onClick={() => setEditing("new")} data-testid="trivia-add"><Plus className="h-4 w-4" /> Add question</button>
          </div>

          {editing && (
            <div className="mt-4">
              <QuestionForm
                key={editing === "new" ? "new" : editing.id}
                initial={editing === "new" ? null : editing}
                categories={meta.categories} difficulties={meta.difficulties} busy={busy}
                onSave={save} onCancel={() => setEditing(null)}
              />
            </div>
          )}

          {shown.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No questions match.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {shown.map((q) => (
                <li key={q.id} className="rounded-xl border border-border bg-card p-3">
                  <div className="flex flex-wrap items-start gap-2">
                    <p className="min-w-0 flex-1 text-sm font-medium">{q.question}</p>
                    <Badge tone={q.status === "published" ? "text-emerald-500" : q.status === "draft" ? "text-amber-500" : ""}>{q.status}</Badge>
                    {q.source === "seed" && <Badge>starter</Badge>}
                    <Badge>{q.category}</Badge>
                    <Badge>{q.difficulty}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Answer: {q.options[q.correct_index]}{q.last_used ? ` · last used ${q.last_used}` : " · never used"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <button className={btn} onClick={() => { setEditing(q); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Edit</button>
                    {q.status !== "published" && (
                      <button className={btn} disabled={busy}
                              onClick={() => act(() => api.post(`/admin/trivia/questions/${q.id}/status`, { status: "published" }), "Published")}>
                        Publish
                      </button>
                    )}
                    {q.status === "published" && (
                      <button className={btn} disabled={busy}
                              onClick={() => act(() => api.post(`/admin/trivia/questions/${q.id}/status`, { status: "draft" }), "Moved to drafts")}>
                        Unpublish
                      </button>
                    )}
                    {q.status !== "retired" && (
                      <button className={btn} disabled={busy}
                              onClick={() => act(() => api.post(`/admin/trivia/questions/${q.id}/status`, { status: "retired" }), "Retired")}>
                        Retire
                      </button>
                    )}
                    {!q.last_used && <ConfirmButton label="Delete" disabled={busy}
                                                    onConfirm={() => act(() => api.delete(`/admin/trivia/questions/${q.id}`), "Deleted")} />}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}